import type { ApiClient } from '../../../shared/api/api-client';

export type AssistantCheckStatus = 'ok' | 'warning' | 'blocked' | 'pending';

export type AssistantCheck = {
  code: string;
  step_id?: string;
  title: string;
  status: AssistantCheckStatus;
  message?: string;
  related_ids?: number[];
  cta?: string;
};

export type AssistantPlanStep = {
  id: string;
  action: string;
  params: Record<string, unknown>;
};

export type AssistantPlan = {
  version: number;
  summary?: string;
  steps: AssistantPlanStep[];
};

export type AssistantStepResult = {
  id: string;
  action: string;
  status: string;
  result?: Record<string, unknown>;
  error?: string;
  checks?: AssistantCheck[];
  warning?: string;
};

export type AssistantPlanResult = {
  status: string;
  steps: AssistantStepResult[];
  failed_step?: string;
  error?: string;
};

export type AssistantOptionItem = {
  id: number;
  name: string;
  branch_id?: number | null;
  branch_ids?: number[];
};

export type AssistantEntityOptions = Record<string, AssistantParamOption[]>;

export type AssistantProposal = {
  plan_id: number;
  conversation_id?: number | null;
  source?: string;
  summary: string;
  checks: AssistantCheck[];
  expires_at: string;
  status?: string;
  result?: AssistantPlanResult;
  plan?: AssistantPlan;
  display_plan?: AssistantPlan;
  /** Справочники для выбора параметров по названию: branch_id, service_id, space_id, trainer_user_id, subscription_plan_id. */
  options?: Record<string, AssistantParamOption[]>;
  /** Подписи ID в шагах: step.id → параметр → название. */
  labels?: Record<string, Record<string, string>>;
};

export type AssistantParamOption = { id: number; name: string; branch_id?: number | null };

export type AssistantReadColumn = {
  key: string;
  title: string;
  kind?: 'text' | 'int' | 'money' | 'pct' | 'date' | 'time';
};

export type AssistantReadResult = {
  call_id?: string;
  tool: string;
  title: string;
  period?: { from: string; to: string };
  columns: AssistantReadColumn[];
  rows: Record<string, unknown>[];
  totals?: Record<string, unknown>;
  notes?: string[];
};

export type AssistantBugReportBadge = {
  id: number;
  category: string;
  summary: string;
  status: string;
};

export type AssistantChatReply = {
  conversation_id: number;
  reply: string;
  status: string;
  usage_id?: number;
  proposal?: AssistantProposal | null;
  action_error?: string;
  failed_step?: string;
  read_results?: AssistantReadResult[];
  bug_report?: AssistantBugReportBadge | null;
};

export type AssistantReadAuditRow = {
  id: number;
  organization_id: number;
  branch_id?: number | null;
  actor_user_id: number;
  actor_name: string;
  actor_role: string;
  conversation_id?: number | null;
  message_id?: number | null;
  tool: string;
  params?: Record<string, unknown>;
  entities?: { type: string; id: number; fields?: string[] }[];
  pii_fields?: string[];
  row_count: number;
  payload_hash: string;
  created_at: string;
};

export type AssistantConversation = {
  id: number;
  title: string;
  updated_at: string;
};

export type AssistantMessage = {
  role: string;
  content: string;
  plan_id?: number | null;
  created_at: string;
};

export type AssistantUserUsage = {
  user_id: number;
  user: string;
  requests: number;
  tokens_in: number;
  tokens_out: number;
  cache_hit_tokens: number;
  cost_rub: number;
};

export type AssistantOrgUsage = {
  organization_id: number;
  organization: string;
  requests: number;
  tokens_in: number;
  tokens_out: number;
  cache_hit_tokens: number;
  cost_rub: number;
  charged_rub?: number;
  included_remaining?: number;
  packs_remaining?: number;
};

export type AssistantRequestRow = {
  id: number;
  organization_id: number;
  organization: string;
  user_id: number;
  user: string;
  intent?: string;
  status: string;
  blocked_reason: string;
  model: string;
  prompt_key: string;
  prompt_hash: string;
  tokens_in: number;
  tokens_out: number;
  cache_hit_tokens: number;
  latency_ms: number;
  cost_usd: number;
  cost_rub: number;
  created_at: string;
};

export type AssistantPromptItem = {
  key: string;
  role: string;
  title: string;
  hash: string;
  source_file: string;
  size_bytes: number;
};

export type AssistantPromptDetail = AssistantPromptItem & { body: string };

export type AssistantProviderInfo = {
  enabled: boolean;
  provider: string;
  model: string;
  thinking: string;
  max_tokens: number;
  prompt_key: string;
  prompts: number;
  anonymize: string;
  egress_log: string;
  retention_d: number;
  pricing: Record<string, number>;
};

function periodQuery(from?: string, to?: string): string {
  const q = new URLSearchParams();
  if (from) q.set('from', from);
  if (to) q.set('to', to);
  const s = q.toString();
  return s ? `?${s}` : '';
}

export async function assistantChat(
  client: ApiClient,
  body: { message: string; conversation_id?: number | null; current_route?: string }
): Promise<AssistantChatReply> {
  return client.request<AssistantChatReply>('/assistant/chat', { method: 'POST', body });
}

export async function fetchAssistantActionPlan(client: ApiClient, id: number): Promise<AssistantProposal> {
  const raw = await client.request<{ proposal?: AssistantProposal }>(`/assistant/action-plans/${id}`, { method: 'GET' });
  if (!raw?.proposal) throw new Error('План не найден');
  return raw.proposal;
}

export async function fetchPendingAssistantActionPlans(client: ApiClient): Promise<AssistantProposal[]> {
  const raw = await client.request<{ items?: AssistantProposal[] }>('/assistant/action-plans/pending', { method: 'GET' });
  return raw?.items ?? [];
}

export async function executeAssistantActionPlan(
  client: ApiClient,
  id: number
): Promise<{ status: string; steps: AssistantStepResult[]; failed_step?: string; proposal?: AssistantProposal }> {
  return client.request(`/assistant/action-plans/${id}/execute`, { method: 'POST', body: {} });
}

export async function cancelAssistantActionPlan(
  client: ApiClient,
  id: number
): Promise<{ status: string; proposal?: AssistantProposal }> {
  return client.request(`/assistant/action-plans/${id}/cancel`, { method: 'POST', body: {} });
}

export async function revalidateAssistantActionPlan(
  client: ApiClient,
  id: number,
  plan: AssistantPlan
): Promise<AssistantProposal> {
  const raw = await client.request<{ proposal?: AssistantProposal }>(`/assistant/action-plans/${id}`, {
    method: 'PATCH',
    body: plan
  });
  if (!raw?.proposal) throw new Error('Не удалось проверить план');
  return raw.proposal;
}

export async function fetchAssistantReadAudit(
  client: ApiClient,
  params: { from?: string; to?: string; user_id?: number; tool?: string; limit?: number; offset?: number } = {}
): Promise<{ items: AssistantReadAuditRow[]; total: number }> {
  const q = new URLSearchParams();
  if (params.from) q.set('from', params.from);
  if (params.to) q.set('to', params.to);
  if (params.user_id) q.set('user_id', String(params.user_id));
  if (params.tool) q.set('tool', params.tool);
  if (params.limit) q.set('limit', String(params.limit));
  if (params.offset) q.set('offset', String(params.offset));
  const raw = await client.request<{ items?: AssistantReadAuditRow[]; total?: number }>(
    `/admin/assistant/read-audit?${q.toString()}`,
    { method: 'GET' }
  );
  return { items: raw?.items ?? [], total: raw?.total ?? 0 };
}

export async function listAssistantConversations(client: ApiClient): Promise<AssistantConversation[]> {
  const raw = await client.request<{ items?: AssistantConversation[] }>('/assistant/conversations', { method: 'GET' });
  return raw?.items ?? [];
}

export async function fetchAssistantConversation(client: ApiClient, id: number): Promise<AssistantMessage[]> {
  const raw = await client.request<{ items?: AssistantMessage[] }>(`/assistant/conversations/${id}`, { method: 'GET' });
  return raw?.items ?? [];
}

export async function fetchOrgAssistantUsage(
  client: ApiClient,
  from?: string,
  to?: string
): Promise<AssistantUserUsage[]> {
  const raw = await client.request<{ items?: AssistantUserUsage[] }>(
    `/admin/assistant/usage${periodQuery(from, to)}`,
    { method: 'GET' }
  );
  return raw?.items ?? [];
}

export async function fetchPlatformAssistantUsage(
  client: ApiClient,
  from?: string,
  to?: string
): Promise<AssistantOrgUsage[]> {
  const raw = await client.request<{ items?: AssistantOrgUsage[] }>(
    `/superadmin/assistant/usage${periodQuery(from, to)}`,
    { method: 'GET' }
  );
  return raw?.items ?? [];
}

export async function fetchPlatformAssistantUsageUsers(
  client: ApiClient,
  organizationId: number,
  from?: string,
  to?: string
): Promise<AssistantUserUsage[]> {
  const sep = periodQuery(from, to) ? '&' : '?';
  const raw = await client.request<{ items?: AssistantUserUsage[] }>(
    `/superadmin/assistant/usage/users?organization_id=${organizationId}${sep}${periodQuery(from, to).replace(/^\?/, '')}`,
    { method: 'GET' }
  );
  return raw?.items ?? [];
}

export async function fetchPlatformAssistantRequests(
  client: ApiClient,
  params: { organization_id?: number; user_id?: number; from?: string; to?: string; limit?: number; offset?: number }
): Promise<{ items: AssistantRequestRow[]; total: number }> {
  const q = new URLSearchParams();
  if (params.organization_id) q.set('organization_id', String(params.organization_id));
  if (params.user_id) q.set('user_id', String(params.user_id));
  if (params.from) q.set('from', params.from);
  if (params.to) q.set('to', params.to);
  if (params.limit) q.set('limit', String(params.limit));
  if (params.offset) q.set('offset', String(params.offset));
  const raw = await client.request<{ items?: AssistantRequestRow[]; total?: number }>(
    `/superadmin/assistant/requests?${q.toString()}`,
    { method: 'GET' }
  );
  return { items: raw?.items ?? [], total: raw?.total ?? 0 };
}

export async function fetchPlatformAssistantRequestDetail(
  client: ApiClient,
  id: number
): Promise<{ request: AssistantRequestRow; request_anonymized: unknown; response_anonymized: unknown }> {
  return client.request(`/superadmin/assistant/requests/${id}`, { method: 'GET' });
}

export async function fetchAssistantPrompts(
  client: ApiClient
): Promise<{ items: AssistantPromptItem[]; rag: Record<string, unknown> }> {
  return client.request('/superadmin/assistant/prompts', { method: 'GET' });
}

export async function fetchAssistantPromptDetail(client: ApiClient, key: string): Promise<AssistantPromptDetail> {
  return client.request<AssistantPromptDetail>(`/superadmin/assistant/prompts/${encodeURIComponent(key)}`, {
    method: 'GET'
  });
}

export async function fetchAssistantProvider(client: ApiClient): Promise<AssistantProviderInfo> {
  return client.request<AssistantProviderInfo>('/superadmin/assistant/provider', { method: 'GET' });
}

export type AssistantQuotaPeriod = {
  starts_at: string;
  ends_at: string;
  included_total: number;
  used: number;
  remaining: number;
  packs_remaining: number;
  source: string;
};

export type AssistantPackRow = {
  id: number;
  name: string;
  requests_total: number;
  requests_used: number;
  price_rub: number;
  valid_until?: string | null;
  status: string;
  created_at: string;
};

export type AssistantPackOffer = {
  id: number;
  name: string;
  requests_total: number;
  price: number;
  validity_days: number;
  is_active: boolean;
  is_archived: boolean;
  sort_order: number;
};

export type AssistantQuotaPayload = {
  period: AssistantQuotaPeriod | null;
  packs: AssistantPackRow[];
  offers: AssistantPackOffer[];
};

export async function fetchOrgAssistantQuota(client: ApiClient): Promise<AssistantQuotaPayload> {
  return client.request<AssistantQuotaPayload>('/admin/assistant/quota', { method: 'GET' });
}

export async function initiateAssistantPackPayment(
  client: ApiClient,
  offerId: number
): Promise<{ payment_url?: string | null }> {
  return client.request('/organizations/me/assistant-packs/initiate-payment', {
    method: 'POST',
    body: { offer_id: offerId, return_url: `${window.location.origin}/tariffs` }
  });
}

export async function fetchPlatformPackOffers(client: ApiClient): Promise<AssistantPackOffer[]> {
  const raw = await client.request<{ items?: AssistantPackOffer[] }>('/superadmin/assistant/pack-offers', {
    method: 'GET'
  });
  return raw?.items ?? [];
}

export async function createPlatformPackOffer(
  client: ApiClient,
  body: Partial<AssistantPackOffer>
): Promise<AssistantPackOffer> {
  return client.request('/superadmin/assistant/pack-offers', { method: 'POST', body });
}

export async function patchPlatformPackOffer(
  client: ApiClient,
  id: number,
  body: Partial<AssistantPackOffer>
): Promise<AssistantPackOffer> {
  return client.request(`/superadmin/assistant/pack-offers/${id}`, { method: 'PATCH', body });
}

export async function runAssistantEval(
  client: ApiClient,
  mode: 'offline' | 'live'
): Promise<{ report: Record<string, unknown> }> {
  return client.request('/superadmin/assistant/eval/run', { method: 'POST', body: { mode } });
}


export async function fetchAssistantOptions(client: ApiClient): Promise<Record<string, AssistantParamOption[]>> {
  const raw = await client.request<Record<string, AssistantParamOption[]>>('/assistant/options', { method: 'GET' });
  return raw ?? {};
}

export type AssistantBugReport = {
  id: number;
  organization_id: number;
  organization?: string;
  branch_id?: number | null;
  user_id: number;
  user?: string;
  conversation_id?: number | null;
  message_id?: number | null;
  category: 'intent_unrecognized' | 'action_error' | 'capability_gap' | 'data_mismatch' | 'user_reported';
  summary: string;
  details?: string;
  user_query?: string;
  assistant_reply?: string;
  current_route?: string;
  diagnostics?: Record<string, unknown>;
  status: 'open' | 'triaged' | 'in_progress' | 'resolved' | 'wontfix';
  feedback_entry_id?: number | null;
  resolved_at?: string | null;
  resolved_by?: number | null;
  created_at: string;
  updated_at: string;
};

export type CreateBugReportPayload = {
  conversation_id?: number | null;
  category?: string;
  summary: string;
  details?: string;
  user_query?: string;
  assistant_reply?: string;
  current_route?: string;
  diagnostics?: Record<string, unknown>;
};

export async function createAssistantBugReport(
  client: ApiClient,
  body: CreateBugReportPayload
): Promise<{ report: AssistantBugReport }> {
  return client.request('/assistant/bug-reports', { method: 'POST', body });
}

export async function fetchPlatformAssistantBugReports(
  client: ApiClient,
  params: { status?: string; category?: string; organization_id?: number; limit?: number; offset?: number } = {}
): Promise<{ items: AssistantBugReport[]; total: number }> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.category) q.set('category', params.category);
  if (params.organization_id) q.set('organization_id', String(params.organization_id));
  if (params.limit) q.set('limit', String(params.limit));
  if (params.offset) q.set('offset', String(params.offset));
  const raw = await client.request<{ items?: AssistantBugReport[]; total?: number }>(
    `/superadmin/assistant/bug-reports?${q.toString()}`,
    { method: 'GET' }
  );
  return { items: raw?.items ?? [], total: raw?.total ?? 0 };
}

export async function patchPlatformAssistantBugReport(
  client: ApiClient,
  id: number,
  status: string
): Promise<{ report: AssistantBugReport }> {
  return client.request(`/superadmin/assistant/bug-reports/${id}`, {
    method: 'PATCH',
    body: { status }
  });
}

export async function syncPlatformAssistantRAG(
  client: ApiClient
): Promise<{ status: string; total_chunks: number; docs_chunks: number; nav_chunks: number; duration_ms: number }> {
  return client.request('/superadmin/assistant/rag/sync', { method: 'POST', body: {} });
}
